import React, { useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, Grid, InputLabel, Paper, Select, TextField, Typography, useTheme, Button, Checkbox, MenuItem, SelectChangeEvent, useMediaQuery } from '@mui/material';
import { IInstrumento } from '../../../services/api/dams/DamServices';
import { useNavigate } from 'react-router';
import { IReading } from '../../../services/api/reading/ReadingServices';
import { Add } from '@mui/icons-material';

interface IInstrumentsCardsProps {
    instrumentos: IInstrumento[]
    urlNav: string
    onDataChange: (dataPiezometro: IReading[]) => void;
    url_selecionada?: string;
}

export const CardPluviometro: React.FC<IInstrumentsCardsProps> = ({ instrumentos, urlNav, onDataChange, url_selecionada }) => {
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [data, setData] = useState<IReading[]>([]);

    const handleChangeSelect = (event: SelectChangeEvent<string>, index: number, fieldName: keyof IReading) => {
        const newValue = event.target.value;
        setData(prevDados => {
            const novoDados = [...prevDados];
            novoDados[index] = { ...novoDados[index], [fieldName]: newValue };
            return novoDados;
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        if (e.target instanceof HTMLInputElement) {
            const { id, value, type, checked } = e.target;
            const convertedValue = type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) : value;
            setData(prevDados => {
                const novoDados = [...prevDados];
                novoDados[index] = { ...novoDados[index], [id]: convertedValue, uuid: instrumentos[index].uuid };
                return novoDados;
            });
        }
    };

    const handleCheckBox = (index: number) => {
        setData(prevDados => {
            const novoDados = [...prevDados];
            const currentSemLeitura = novoDados[index]?.sem_leitura || false;
            novoDados[index] = { ...novoDados[index], sem_leitura: !currentSemLeitura };

            if (!currentSemLeitura) { 
                novoDados[index].data_leitura = undefined;
                novoDados[index].condicao_leitura = undefined;
                novoDados[index].leitura_diaria = undefined;
                novoDados[index].comentario = undefined;
                novoDados[index].responsavel_leitura = undefined;
                novoDados[index].nivel_alerta = undefined; 
            }
            return novoDados;
        });
    };

    useEffect(() => {
        onDataChange(data);
    }, [data, onDataChange]);

    if (instrumentos === undefined || instrumentos.length === 0) {
        return (
            <Box display={'flex'} justifyContent={'center'} alignItems={'start'}>
                <Button variant="contained" color="primary" onClick={() => navigate(urlNav)} sx={{ borderRadius: '25px' }} startIcon={<Add />}>
                    Adicionar Instrumento
                </Button>
            </Box>
        );
    }

    return (
        <Box
            display={'flex'}
            justifyContent={'center'}
            flexDirection={'column'}
            alignItems={'center'}
            width={'100%'}
            gap={2}
        >
            {instrumentos.map((instrumento, index) => (
                <Box key={index} display={'flex'} width={'83%'} component={Paper} padding={2} variant='outlined' borderRadius={5} marginBottom={2} sx={{ borderColor: `${url_selecionada === instrumento.uuid ? 'red' : undefined}` }}>
                    <Grid container direction={'row'} spacing={2}>
                        <Grid item xs={12}>
                            <Grid container direction={'row'} alignItems={'center'} gap={2}>
                                <Typography variant='h6' color={theme.palette.primary.main}>{instrumento.nome}</Typography>
                                <FormControlLabel
                                    control={<Checkbox checked={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false} onChange={() => handleCheckBox(index)} />}
                                    label='Sem medição deste instrumento'
                                    id='sem_leitura'
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={smDown ? 12 : 6}>
                            <TextField
                                type="datetime-local"
                                id="data_leitura"
                                label="Data e hora de leitura"
                                fullWidth
                                value={data[index]?.data_leitura || ''}
                                onChange={(e) => handleChange(e, index)}
                                disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={smDown ? 12 : 6}>
                            <FormControl fullWidth disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false}>
                                <InputLabel id='condicao_leitura-label'>Condição de leitura</InputLabel>
                                <Select
                                    labelId='condicao_leitura-label'
                                    id='condicao_leitura'
                                    label='Condição de leitura'
                                    value={data[index]?.condicao_leitura || ''}
                                    onChange={(e) => handleChangeSelect(e, index, 'condicao_leitura')}
                                >
                                    <MenuItem value='seco'><Typography>Seco</Typography></MenuItem>
                                    <MenuItem value='agua'><Typography>Com nível da água</Typography></MenuItem>
                                    <MenuItem value='nao_aplica'><Typography>Não se aplica</Typography></MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField type='text' id='leitura_diaria' fullWidth label="Leitura diária em mm" value={data[index]?.leitura_diaria || ''} onChange={(e) => handleChange(e, index)} disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField type='text' id='comentario' fullWidth label="Comentário sobre a leitura" value={data[index]?.comentario || ''} onChange={(e) => handleChange(e, index)} disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField type='text' id='responsavel_leitura' fullWidth label="Quem realizou a leitura" required value={data[index]?.responsavel_leitura || ''} onChange={(e) => handleChange(e, index)} disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required disabled={data[index]?.sem_leitura || url_selecionada !== undefined ? url_selecionada === instrumento.uuid ? false : true : false}>
                                <InputLabel id="nivel_alerta-label">Nível de alerta</InputLabel>
                                <Select
                                    labelId="nivel_alerta-label"
                                    id="nivel_alerta"
                                    label="Nível de alerta"
                                    value={data[index]?.nivel_alerta || ''}
                                    onChange={(e) => handleChangeSelect(e, index, 'nivel_alerta')}
                                >
                                    <MenuItem value="Normal">Normal</MenuItem>
                                    <MenuItem value="Atenção">Atenção</MenuItem>
                                    <MenuItem value="Alerta">Alerta</MenuItem>
                                    <MenuItem value="Emergência">Emergência</MenuItem>
                                    <MenuItem value="Não há limite de referência estabelecido">Não há limite de referência estabelecido</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            ))}
        </Box>
    );
};